import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { useDebouncedCallback } from 'use-debounce'
import TopNavigation from '../../components/Navigation/TopNavigation'
import ScrollToTopOnMount from '../../components/Effects/ScrollToTopOnMount'
import PageContent from '../../components/Page/PageContent'
import PageHeader from '../../components/Page/PageHeader'
import PageLoader from '../../components/Page/PageLoader'
import ResourceTable from '../../components/Resource/ResourceTable'
import getBookingTableRenderer from '../../components/Resource/BookingRenderer'
import CardEmptyInfo from '../../components/Card/CardEmptyInfo'
import Notification from '../../utilities/Notification'
import LocalStorage, { LocalStorageKey } from '../../LocalStorage'
import { BookingsController, EventTypesController } from '../../controllers'
import ERoute from '../../ERoute'
import RouteHelper from '../../helpers/RouteHelper'
import { AppState } from '../../store'
import { showSendEmailModal } from '../../store/modals/actions'
import { ModalsActionTypes } from '../../store/modals/types'
import { locationLabel } from '../../components/Scheduling/Booker/format'
import { Booking, BookingFilter, BookingFilterProperty, BookingPeriod, BookingStatus, EventType, EventTypeLocationType, ResourceListFilterType } from '../../types'

export default function Bookings() {
  const { t } = useTranslation()
  const history = useHistory()
  const storeDispatch = useDispatch() as (action: ModalsActionTypes) => void
  const setting = useSelector((state: AppState) => state.authentication.currentUser.workspace.setting)
  const [bookings, setBookings] = React.useState<Booking[]>([])
  const [eventTypes, setEventTypes] = React.useState<EventType[]>([])
  const [search, setSearch] = React.useState('')
  const [filters, setFilters] = React.useState<{ [key in BookingFilterProperty]?: any }>({})
  // '-' orders by booking date: soonest first for upcoming bookings, most recent first otherwise
  const [sortValue, setSortValue] = React.useState<string>(LocalStorage.get(LocalStorageKey.BOOKING_SORT_VALUE, '-'))
  const [currentPage, setCurrentPage] = React.useState(0)
  const [totalPages, setTotalPages] = React.useState(0)
  const [perPage, setPerPage] = React.useState<number>(LocalStorage.get(LocalStorageKey.PER_PAGE, 25))
  const [didInitialLoad, setDidInitialLoad] = React.useState(false)
  const [isFetching, setIsFetching] = React.useState(false)
  const [, forceUpdate] = React.useReducer(x => x + 1, 0)

  const filtersActive = search.length > 0 || Object.keys(filters).length > 0

  const fetchBookings = async (page: number) => {
    setIsFetching(true)
    try {
      const response = await BookingsController.getBookings({
        page: page,
        per_page: perPage,
        search: search,
        order: sortValue,
        ...filters,
      })

      if (Array.isArray(response.bookings)) {
        setBookings(response.bookings)
        setCurrentPage(response.current_page)
        setTotalPages(response.total_pages)
      } else {
        Notification.notifyError(t('Bookings::Could not load bookings'))
      }
    } catch (ex) {
      console.error(ex)
      Notification.notifyError(t('Bookings::Could not load bookings'))
    } finally {
      setIsFetching(false)
      setDidInitialLoad(true)
    }
  }

  const debouncedFetchBookings = useDebouncedCallback((page: number) => fetchBookings(page), 250)

  React.useEffect(() => { debouncedFetchBookings(1) }, [search, filters, perPage, sortValue])

  React.useEffect(() => {
    EventTypesController.getEventTypes({ per_page: 200 })
      .then(response => { if (Array.isArray(response.event_types)) setEventTypes(response.event_types) })
      .catch(console.error)
  }, [])

  const onPerPageChange = (value: number) => {
    LocalStorage.set(LocalStorageKey.PER_PAGE, value)
    setPerPage(value)
  }

  const onSortValueChange = (value: string) => {
    LocalStorage.set(LocalStorageKey.BOOKING_SORT_VALUE, value)
    setSortValue(value)
  }

  const onClearFilters = () => {
    setSearch('')
    setFilters({})
  }

  const onActionClick = (key: string, booking: Booking) => {
    switch (key) {
      case 'join':
        if (booking.meeting_link) window.open(booking.meeting_link, '_blank', 'noopener')
        break
      case 'email':
        if (booking.contact_id) storeDispatch(showSendEmailModal({ email: { to: [booking.contact_id] } }))
        break
      case 'contact':
        if (booking.contact_id) history.push(RouteHelper.process(ERoute.PATH_CONTACT, { id: booking.contact_id }))
        break
      case 'event_type':
        history.push(RouteHelper.process(ERoute.PATH_SCHEDULING_EVENT_TYPE, { id: booking.event_type.id }))
        break
    }
  }

  const onColumnsChange = (columns: string[]) => {
    LocalStorage.set(LocalStorageKey.BOOKING_COLUMNS, columns)
    forceUpdate()
  }

  const bookingFilters: BookingFilter[] = [
    {
      name: 'period',
      label: t('Bookings::Period'),
      type: ResourceListFilterType.SINGLE_OPTION,
      options: [
        { label: t('Bookings::Upcoming'), value: BookingPeriod.UPCOMING },
        { label: t('Bookings::Past'), value: BookingPeriod.PAST },
      ],
    },
    { name: 'start_time', label: t('Bookings::Date'), type: ResourceListFilterType.DATE },
    {
      name: 'event_type_id',
      label: t('Bookings::Event type'),
      type: ResourceListFilterType.SINGLE_OPTION,
      options: eventTypes.map(eventType => ({ label: eventType.title, value: eventType.id })),
    },
    { name: 'host_id', label: t('Bookings::Organiser'), type: ResourceListFilterType.SINGLE_RESOURCE, resourceType: 'user', isValidNewOption: () => false },
    { name: 'contact_id', label: t('Bookings::Contact'), type: ResourceListFilterType.SINGLE_RESOURCE, resourceType: 'contact', isValidNewOption: () => false },
    { name: 'booker_name', label: t('Bookings::Booker name'), type: ResourceListFilterType.STRING },
    { name: 'booker_email', label: t('Bookings::Booker email'), type: ResourceListFilterType.STRING },
    {
      name: 'status',
      label: t('Bookings::Status'),
      type: ResourceListFilterType.SINGLE_OPTION,
      options: [{ label: t('Bookings::Accepted'), value: BookingStatus.ACCEPTED }],
    },
    { name: 'created_at', label: t('Bookings::Booked on'), type: ResourceListFilterType.DATE },
    { name: 'guests', label: t('Bookings::Guests'), type: ResourceListFilterType.STRING },
    {
      name: 'location',
      label: t('Bookings::Location'),
      type: ResourceListFilterType.SINGLE_OPTION,
      options: Object.values(EventTypeLocationType).map(location => ({ label: locationLabel(t, location), value: location })),
    },
  ]

  const { headers, columns, selectedColumns, renderRow } = getBookingTableRenderer({
    setting: setting,
    onActionClick: onActionClick,
  })

  return (
    <>
      <Helmet>
        <title>{t('Bookings::{{__appName}} | Bookings')}</title>
      </Helmet>
      <ScrollToTopOnMount />

      <TopNavigation
        icon='calendar-clock'
        title={t('Bookings::Scheduling')}
      />

      <PageContent>
        <PageHeader title={t('Bookings::Bookings')} />

        {!didInitialLoad && <PageLoader />}
        {didInitialLoad && <ResourceTable
          data={bookings}
          headers={headers}
          renderRow={renderRow}
          renderEmpty={<CardEmptyInfo
            icon={filtersActive ? 'search' : 'calendar-clock'}
            description={filtersActive
              ? t('Bookings::No bookings found')
              : t('Bookings::You have no bookings')}
            descriptionActionText={filtersActive ? t('Bookings::Clear filters') : t('Bookings::View event types')}
            onDescriptionActionClick={filtersActive ? onClearFilters : () => history.push(ERoute.PATH_SCHEDULING_EVENT_TYPES)}
          />}
          filters={bookingFilters}
          activeFilters={filters}
          onFiltersChange={setFilters}
          sortOptions={[
            { label: '-', value: '-' },
            { label: t('Bookings::Date ↑'), value: 'start_time_asc' },
            { label: t('Bookings::Date ↓'), value: 'start_time_desc' },
            { label: t('Bookings::Booked on ↑'), value: 'created_at_asc' },
            { label: t('Bookings::Booked on ↓'), value: 'created_at_desc' },
          ]}
          sortValue={sortValue}
          onSortChange={onSortValueChange}
          pagination={{ page: currentPage, perPage: perPage, pageCount: totalPages }}
          onPageChange={(page) => fetchBookings(page)}
          onPerPageChange={onPerPageChange}
          isLoading={isFetching}
          searchValue={search}
          onSearchChange={setSearch}
          onSearchSubmit={setSearch}
          columns={columns}
          selectedColumns={selectedColumns}
          onColumnsChange={onColumnsChange}
          stickyHeader={true}
          maxHeight='65vh'
        />}
      </PageContent>
    </>
  )
}
